// Declaration parser for `--name: value` extraction.
// It supports nested scopes and quoted segments in values.
import type { ParsedVariableDeclaration } from './types'
import { startsVariableDeclaration } from './root'

// Regex validates custom property names like `--token-name`.
const variableNameRegex = /^--[a-zA-Z_][\w-]*$/

// Parse leading `--name: value` declarations while preserving nested scopes.
export function extractLeadingVariableDeclarations(text: string): {
  declarations: ParsedVariableDeclaration[]
  remainder: string
} {
  const declarations: ParsedVariableDeclaration[] = []
  let remaining = text.trim()

  // Keep parsing while the remaining text starts with a variable declaration.
  while (startsVariableDeclaration(remaining)) {
    const parsed = parseOneVariableDeclaration(remaining)
    // Stop when parser cannot complete current declaration yet.
    if (!parsed) {
      break
    } else {
      // Keep collecting parsed declaration entries.
    }

    declarations.push({ name: parsed.name, value: parsed.value })
    remaining = parsed.remainder.trimStart()
  }

  // Return parsed declarations and unmatched remainder.
  return {
    declarations,
    remainder: remaining,
  }
}

// Parse one declaration and return remainder after its terminator.
function parseOneVariableDeclaration(
  text: string,
): { name: string; value: string; remainder: string } | null {
  const colonIndex = text.indexOf(':')
  // Reject malformed declarations without `--x: value` shape.
  if (colonIndex <= 2) {
    return null
  } else {
    // Continue declaration name validation.
  }

  const name = text.slice(0, colonIndex).trim()
  // Reject names that do not match custom property syntax.
  if (!variableNameRegex.test(name)) {
    return null
  } else {
    // Continue declaration value scanning.
  }

  let depthParen = 0
  let depthBracket = 0
  let depthBrace = 0
  let quote: "'" | '"' | null = null
  let endIndex = -1

  // Scan declaration value and stop at top-level semicolon.
  for (let i = colonIndex + 1; i < text.length; i += 1) {
    const char = text[i]
    const previous = i > colonIndex + 1 ? text[i - 1] : ''

    // Keep quoted content opaque until matching quote closes.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else {
        // Keep scanning quoted content.
      }
      continue
    } else {
      // Continue depth tracking outside quotes.
    }

    // Enter quote mode when parser meets an opening quote.
    if (char === "'" || char === '"') {
      quote = char
      continue
    } else {
      // Continue nested scope tracking.
    }

    // Track nested scopes so internal semicolons are ignored.
    if (char === '(') {
      depthParen += 1
    } else if (char === ')' && depthParen > 0) {
      depthParen -= 1
    } else if (char === '[') {
      depthBracket += 1
    } else if (char === ']' && depthBracket > 0) {
      depthBracket -= 1
    } else if (char === '{') {
      depthBrace += 1
    } else if (char === '}' && depthBrace > 0) {
      depthBrace -= 1
    } else {
      // Keep depths unchanged for non-structural chars.
    }

    // Finalize declaration only on top-level semicolon.
    if (char === ';' && depthParen === 0 && depthBracket === 0 && depthBrace === 0) {
      endIndex = i
      break
    } else {
      // Continue scanning declaration value body.
    }
  }

  // Allow semicolon-less declarations only when scopes are balanced.
  if (endIndex === -1) {
    if (quote || depthParen > 0 || depthBracket > 0 || depthBrace > 0) {
      return null
    } else {
      endIndex = text.length
    }
  } else {
    // Continue value extraction for explicit semicolon case.
  }

  const value = text.slice(colonIndex + 1, endIndex).trim()
  // Reject empty values to avoid storing invalid declarations.
  if (!value) {
    return null
  } else {
    // Continue remainder extraction for parsed declaration.
  }

  const endedWithSemicolon = endIndex < text.length && text[endIndex] === ';'
  const remainderStart = endedWithSemicolon ? endIndex + 1 : endIndex
  // Return parsed declaration and remaining source suffix.
  return {
    name,
    value,
    remainder: text.slice(remainderStart),
  }
}
